import {
  MOTION_GRAPHICS_CATEGORIES,
  MOTION_GRAPHICS_COLLECTION,
  MUSIC_CATEGORIES,
  MUSIC_COLLECTION,
  MUSIC_COLLECTIONS,
  MUSIC_CONTRIBUTORS,
  MUSIC_QUALIFIED_KEYWORDS,
  MUSIC_TAGS,
  SFX_CATEGORIES,
  SFX_COLLECTION,
  SFX_COLLECTIONS,
  SFX_QUALIFIED_KEYWORDS,
  SFX_TAGS,
  STOCK_VIDEO_CATEGORIES,
  STOCK_VIDEO_COLLECTION,
} from '@music-vine/setlist';

import type { GenericSearchResult } from '@/features/SearchPage/types';
import type { SearchResponse } from '@music-vine/setlist';

// Synthetic collection name applied to motion-graphics responses when the
// caller wanted LUTs. It shares MOTION_GRAPHICS_COLLECTION's Typesense
// documents but a distinct label lets the merger route it to the LUTs
// section instead of pooling with regular motion graphics.
export const LUTS_MG_COLLECTION_MARKER = 'motiongraphics.v2:luts';

const collectionsToMerge = [
  {
    name: 'Popular Music Searches',
    collections: [MUSIC_QUALIFIED_KEYWORDS, MUSIC_TAGS],
    limit: 6,
  },
  {
    name: 'Popular SFX Searches',
    collections: [SFX_QUALIFIED_KEYWORDS, SFX_TAGS],
    limit: 6,
  },
  {
    name: 'Playlists',
    collections: [MUSIC_COLLECTIONS, MUSIC_CATEGORIES],
    limit: 12,
  },
  {
    name: 'SFX Collections',
    collections: [SFX_COLLECTIONS, SFX_CATEGORIES],
    limit: 12,
  },
  {
    name: 'Artists',
    collections: [MUSIC_CONTRIBUTORS],
  },
  {
    name: 'Tracks',
    collections: [MUSIC_COLLECTION],
  },
  {
    name: 'SFX',
    collections: [SFX_COLLECTION],
  },
  {
    name: 'Popular Motion Graphics Searches',
    collections: [MOTION_GRAPHICS_CATEGORIES],
  },
  {
    name: 'Motion Assets',
    collections: [MOTION_GRAPHICS_COLLECTION],
    limit: 6,
  },
  {
    name: 'Popular Stock Video Searches',
    collections: [STOCK_VIDEO_CATEGORIES],
  },
  {
    name: 'Stock Video Assets',
    collections: [STOCK_VIDEO_COLLECTION],
    limit: 6,
  },
  {
    name: 'LUTs Assets',
    collections: [LUTS_MG_COLLECTION_MARKER],
    limit: 6,
  },
];

export const mergeCollectionHits = (
  sortedResults: SearchResponse<GenericSearchResult>[]
): SearchResponse<GenericSearchResult>[] => {
  const mergedResults: SearchResponse<GenericSearchResult>[] = [];

  collectionsToMerge.forEach((item) => {
    const toMerge = sortedResults.filter((result) => {
      const collectionName = result.request_params?.collection_name;

      return collectionName && item.collections.includes(collectionName);
    });

    if (toMerge.length === 0) {
      return;
    }

    // Merge hits and rename collection.
    const mergedHits = toMerge.reduce((acc, curr) => {
      const hits = [...(curr.hits ?? []), ...(acc.hits ?? [])];

      // Use title for stock videos, name for other assets
      const uniqueHits = [
        ...new Map(
          hits.map((v) => {
            const doc = v.document as { name?: string; title?: string };
            const key = (doc.title ?? doc.name)?.toLowerCase();
            return [key, v];
          })
        ).values(),
      ];

      return {
        ...acc,
        hits: [...uniqueHits],
      };
    });

    const sortedHits = mergedHits.hits?.sort((a, b) => {
      // For collections that should prioritize exact name matches, use field weight first
      if (item.name === 'Playlists' || item.name === 'SFX Collections') {
        const aWeight = a.text_match_info?.best_field_weight || 0;
        const bWeight = b.text_match_info?.best_field_weight || 0;

        // First sort by field weight (higher is better)
        if (aWeight !== bWeight) {
          return bWeight - aWeight;
        }

        // Then sort by text match score (using the score field for consistency)
        const aScore = Number.parseFloat(a.text_match_info?.score || '0');
        const bScore = Number.parseFloat(b.text_match_info?.score || '0');
        return bScore - aScore;
      }

      // For other collection types, use text_match for sorting
      return b.text_match - a.text_match;
    });

    if (sortedHits && item.limit && sortedHits.length > item.limit) {
      sortedHits.length = item.limit;
    }

    const newHits = {
      ...mergedHits,
      hits: sortedHits,
      request_params: {
        ...mergedHits.request_params,
        collection_name: item.name,
      },
    };

    mergedResults.push(newHits);
  });

  const fieldsToRemove = collectionsToMerge.reduce<string[]>(
    (acc, curr) => [...acc, ...(curr.collections ?? [])],
    []
  );

  const filteredResults = mergedResults.filter((sortedResult) => {
    const name = sortedResult.request_params?.collection_name;

    return name && !fieldsToRemove.includes(name);
  });

  return filteredResults;
};
